import 'gsap/Draggable';
import { TweenLite } from 'gsap';

class DragDrop {
	constructor(el) {
		this.el = el;
		this.sizeMaxDisplay = 5;
		this.rowSize = 38;
		this.heightDisplayList = this.rowSize * this.sizeMaxDisplay;
		this.containers = [];
		this.containers.get = i => this.containers[i];
		this.scrollable = true;
		this.colours = {
			lightblue1: '#EEF6FF',
			lightblue2: '#fafbfc'
		};
		this.boxShadows = {
			transparent: '0 4px 7px 2px rgba(0, 0, 0, 0)',
			light: '0 4px 7px 2px rgba(0, 0, 0, 0.1)'
		};

		this.init();
	}

	getContaners() {
		return this.containers;
	}

	init() {
		Array.from(this.el.querySelectorAll('.js-dragdrop')).forEach(node => {
			let container;

			this.containers.push((container = this.createContainer(node)));

			Array.from(node.children).forEach(child => {
				container.insertItem(child);
			});

			container.indexItems();
		});

		const listener = e => {
			if (!this.scrollable) {
				e.preventDefault();
			}
		};

		if (window.Modernizr.touch) {
			document.addEventListener('touchmove', listener, {
				passive: false
			});
		}
	}

	createContainer(element) {
		let index = this.containers.length;
		let sortables = []; // Array of sortables
		let container = {
			index: index,
			element: element,
			sortables: sortables,
			indexItems: indexItems.bind(this),
			insertItem: insertItem.bind(this)
		};

		TweenLite.to(element, 0.5, { autoAlpha: 1 });

		function indexItems() {
			sortables.forEach((s, i) => s.setIndex(i));
		}

		function insertItem(node) {
			container.sortables.push(
				this.sortableFn(
					node,
					container.sortables.length,
					container.index
				)
			);
		}

		return container;
	}

	changeIndex(item, to) {
		let container = this.containers.get(item.col);

		// Change position in array
		this.arrayMove(container.sortables, item.index, to);

		// Set index for each sortable
		container.indexItems();
	}

	getBackgroundItem(col) {
		// Returns the background based on the column
		const background =
			col === 0 ? this.colours.lightblue1 : this.colours.lightblue2;
		return background;
	}

	sortableFn(element, index, col) {
		const content = element.querySelector('.item-content');
		const order = element.querySelector('.order');

		TweenLite.set(content, {
			boxShadow: this.boxShadows.transparent,
			backgroundColor: this.getBackgroundItem(col)
		});

		const dragger = new window.Draggable(element, {
			onDragStart: onDragStart.bind(this),
			onRelease: onRelease.bind(this),
			autoScroll: 1,
			onDrag: onDrag.bind(this),
			cursor: 'grab',
			type: 'y'
		});

		// Public properties and methods
		const sortable = {
			dragger: dragger,
			element: element,
			index: index,
			col: col,
			setIndex: setIndex.bind(this)
		};

		TweenLite.set(element, { y: index * this.rowSize, x: 0 });
		TweenLite.set(this.containers.get(sortable.col).element, {
			height: (index + 1) * this.rowSize
		});

		function setIndex(index) {
			sortable.index = index;
			order.textContent = index + 1;
			element.setAttribute('data-index', index);

			// Don't layout if you're dragging
			if (!dragger.isDragging) {
				layout.bind(this).call();
			}
		}

		function onDragStart() {
			sortable.initCol = sortable.col;

			const animationStart = TweenLite.to(content, 0.3, {
				force3D: true,
				scale: 1.05,
				boxShadow: this.boxShadows.light,
				backgroundColor: this.colours.lightblue1,
				paused: true
			});

			animationStart.play();

			dragger.update();
		}

		function onDrag() {
			this.scrollable = false;
			let container = this.containers.get(sortable.col);
			let last = container;

			this.containers.forEach(cont => {
				if (
					cont !== container &&
					dragger.hitTest(cont.element, '51%')
				) {
					container = cont;
					sortable.col = cont.index;
				}
			});

			if (container !== last) {
				// Remove the sortable from the last container;
				last.sortables.splice(sortable.index, 1);

				// Make sure to not insert the sortable at an index higher than the next this.containers length
				if (sortable.index > container.sortables.length - 1) {
					sortable.index = container.sortables.length;
				}

				// Insert the sortable into the new this.containers list
				container.sortables.splice(sortable.index, 0, sortable);

				// --------------------------------
				// >> Handle only 5 elements
				// --------------------------------
				if (
					container.index === 0 &&
					container.sortables.length - 1 === this.sizeMaxDisplay
				) {
					// Remove last item from container
					const lastItem = [...container.sortables].pop();
					lastItem.index = lastItem.index + 1;
					container.sortables.splice(lastItem.index, 1);

					// Insert the removed item to the beginning of the other array
					last.sortables.unshift(lastItem);
					lastItem.initCol = 1;
					lastItem.col = 1;

					// Re Index
					last.indexItems();

					// Append element to DOM
					last.element.appendChild(lastItem.element);

					// Set position at the top
					TweenLite.fromTo(lastItem.element, 1, { y: -20 }, { y: 0 });

					// Change background colour
					const lastContent = lastItem.element.querySelector(
						'.item-content'
					);
					TweenLite.to(lastContent, 0.3, {
						backgroundColor: this.getBackgroundItem(lastItem.col)
					});
				}

				// Re index the last this.containers items, so that the empty space is removed
				last.indexItems();

				// To keep the drag target's position
				if (container.element !== sortable.element.parentNode) {
					container.element.appendChild(sortable.element);
					dragger.endDrag();
					// container.element.parentNode.classList.add('border');

					// Compensate for the directional shift in X
					const height = sortable.element.clientHeight;
					const newX = dragger.x;
					const newY =
						container.index < last.index
							? this.heightDisplayList - (height - height / 2)
							: height / 2 - height;

					// TweenLite.set(sortable.element, { x: 0, y: 0 });
					TweenLite.set(sortable.element, { x: newX, y: newY });

					dragger.startDrag(dragger.pointerEvent);
				}
			}

			// Calculate the current index based on element's position
			const index = this.clamp(
				Math.round(dragger.y / this.rowSize),
				0,
				container.sortables.length - 1
			);

			if (index !== sortable.index) {
				this.changeIndex(sortable, index);
			}
		}

		function onRelease() {
			this.scrollable = true;

			if (sortable.initCol !== sortable.col) {
				let container = this.containers.get(sortable.col);
				container.indexItems();
			} else {
				layout.bind(this).call();
			}

			// if (sortable.col == 0) {
			//   this.containers.get(sortable.col).element.parentNode.classList.remove('border');
			// }

			const animationRelease = TweenLite.to(content, 0.3, {
				force3D: true,
				scale: 1,
				boxShadow: this.boxShadows.transparent,
				backgroundColor: this.getBackgroundItem(sortable.col),
				paused: true
			});

			animationRelease.play();
		}

		function layout() {
			TweenLite.to(element, 0.3, {
				y: sortable.index * this.rowSize,
				x: 0
			});

			// Update height Column
			let container = this.containers.get(sortable.col);
			TweenLite.to(container.element, 0.3, {
				height: container.sortables.length * this.rowSize
			});
		}

		return sortable;
	}

	arrayMove(array, from, to) {
		// Changes an elements's position in array
		array.splice(to, 0, array.splice(from, 1)[0]);
	}

	clamp(value, a, b) {
		// Clamps a value to a min/max
		return value < a ? a : value > b ? b : value;
	}
}

export default DragDrop;
